import {
  Client,
  ClientResponse,
  Conversation,
  ConversationEventType,
  ConversationActionParams,
  ConversationMessage,
  ConversationParticipant,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  SendConversationMessageParams,
  SendFeedbackParams,
  StartConversationParams,
  StartConversationResponse,
  SendConversationTypingParams,
} from '@stripe-internal/conversations-client';
import {ActionHandler} from './Action';
import {GetConversationMessagesHandler} from './GetConversationMessages';
import {GetConversationParticipantsHandler} from './GetConversationParticipants';
import {LeaveConversationHandler} from './LeaveConversation';
import {PollConversationHandler} from './PollConversation';
import {SendConversationMessageHandler} from './SendConversationMessage';
import {SendFeedbackHandler} from './SendFeedback';
import {StartConversationHandler} from './StartConversation';
import type {StartConversationCreateProcessFailureMetricTags} from './StartConversation';
import {ApiMethods, ReportWarning} from './ApiMethods';
import {InProgressHandler} from './InProgress';
import {SendConversationTypingHandler} from './SendConversationTyping';

/**
 * A collection of handler functions that implement the Client interface methods.
 *
 * Each handler transforms between the client-side conversation framework constructs
 * and API constructs - handling parameter validation, API request construction,
 * and response transformation via FrameworkAdapter.
 */
export type ClientHandlers = {
  startConversation: (
    params: StartConversationParams,
  ) => Promise<StartConversationResponse>;
  leaveConversation: (
    params: LeaveConversationParams,
  ) => Promise<ClientResponse>;
  getConversationMessages: (
    params: GetConversationMessagesParams,
  ) => Promise<GetConversationMessagesResponse>;
  getConversationParticipants: (
    params?: GetConversationParticipantsParams,
  ) => Promise<GetConversationParticipantsResponse>;
  sendConversationMessage: (
    params: SendConversationMessageParams,
  ) => Promise<ClientResponse>;
  sendFeedback: (params: SendFeedbackParams) => Promise<ClientResponse>;
  action: (params: ConversationActionParams) => Promise<ClientResponse>;
  inProgress: (params: InProgressParams) => Promise<InProgressResponse>;
  sendConversationTyping: (
    params?: SendConversationTypingParams,
  ) => Promise<ClientResponse>;
  pollConversation: (params: {
    conversationId: string;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
  }) => Promise<
    | {
        type: 'success';
        messages: Record<string, ConversationMessage>;
        shouldPoll: boolean;
      }
    | {type: 'error'; error: string; code: number}
  >;
};

/**
 * Builds a set of client handlers that implement conversation operations.
 *
 * This factory is the dependency boundary between client.ts and operation
 * handlers. The parent branch keeps the handlers as stubs while sibling
 * branches wire their implemented handlers to ApiMethods, event emitters, and
 * callbacks.
 *
 * @param request - The fetch function used by ApiMethods to make HTTP requests
 * @param reportWarning - Reports non-fatal API parsing and adapter warnings
 * @param emitConversationEvent - Emits conversation events produced by handlers
 * @param baseUrl - Optional Conversations API base URL override
 * @returns A ClientHandlers object with methods for all conversation operations
 */
export function buildClientHandlers({
  client,
  request,
  reportWarning,
  emitConversationEvent,
  emitDelegateFirstMessageToServerFailureMetric,
  getParticipant,
  baseUrl,
}: {
  client: Client;
  request: (url: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  reportWarning?: ReportWarning;
  emitConversationEvent: (event: ConversationEventType) => void;
  emitDelegateFirstMessageToServerFailureMetric?: (
    tags: StartConversationCreateProcessFailureMetricTags,
  ) => void;
  getParticipant: () => ConversationParticipant | undefined;
  baseUrl?: string;
}): ClientHandlers {
  const apiMethods = new ApiMethods({
    fetcher: request,
    reportWarning,
    baseUrl,
  });
  const leaveConversationHandler = new LeaveConversationHandler({
    deleteConversation: (conversationId) =>
      apiMethods.deleteConversation(conversationId),
    emitConversationEvent,
    reportWarning,
  });
  const sendConversationMessageHandler = new SendConversationMessageHandler({
    sendMessage: (conversationId, payload) =>
      apiMethods.sendMessage(conversationId, payload),
    emitConversationEvent,
    reportWarning,
  });
  const startConversationHandler = new StartConversationHandler({
    createConversation: (payload) => apiMethods.createConversation(payload),
    sendMessage: (conversationId, payload) =>
      apiMethods.sendMessage(conversationId, payload),
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
    emitDelegateFirstMessageToServerFailureMetric,
    buildConversationInstance: ({frameworkConversation, params}) => {
      return new Conversation(client, {
        id: frameworkConversation.id,
        user: params.participant,
        status: frameworkConversation.status,
        syncStorage: params.syncStorage,
        messages: frameworkConversation.messages,
        participants: frameworkConversation.participants,
        context: frameworkConversation.context,
      });
    },
  });
  const getConversationMessagesHandler = new GetConversationMessagesHandler({
    getConversation: (conversationId) =>
      apiMethods.getConversation(conversationId),
  });
  const getConversationParticipantsHandler =
    new GetConversationParticipantsHandler({
      getConversation: (conversationId) =>
        apiMethods.getConversation(conversationId),
    });
  const sendFeedbackHandler = new SendFeedbackHandler({
    sendMessage: (conversationId, payload, options) =>
      apiMethods.sendMessage(conversationId, payload, options),
    emitConversationEvent,
    getParticipant,
  });
  const actionHandler = new ActionHandler({
    sendMessage: (conversationId, payload) =>
      apiMethods.sendMessage(conversationId, payload),
    emitConversationEvent,
    reportWarning,
    getParticipant,
  });
  const inProgressHandler = new InProgressHandler({
    listConversations: (params) => apiMethods.listConversations(params),
  });
  const sendConversationTypingHandler = new SendConversationTypingHandler();
  const pollConversationHandler = new PollConversationHandler({
    pollConversation: (conversationId) =>
      apiMethods.pollConversation(conversationId),
  });

  return {
    startConversation: (params) => startConversationHandler.call(params),
    leaveConversation: (params) => leaveConversationHandler.call(params),
    getConversationMessages: (params) =>
      getConversationMessagesHandler.call(params),
    getConversationParticipants: (params) =>
      getConversationParticipantsHandler.call(params),
    sendConversationMessage: (params) =>
      sendConversationMessageHandler.call(params),
    sendFeedback: (params) => sendFeedbackHandler.call(params),
    action: (params) => actionHandler.call(params),
    inProgress: (params) => inProgressHandler.call(params),
    sendConversationTyping: (params) =>
      sendConversationTypingHandler.call(params),
    pollConversation: (params) => pollConversationHandler.call(params),
  };
}
